import React from 'react';
import { appToast } from '../../../../components/AppToast';

export default function BatchPayslipModal({ isOpen, onClose, record = null, allRecords = [], month = 'Jul', year = '2026' }) {
  if (!isOpen) return null;

  const targetRecord = record || allRecords[0] || {
    idCard: '',
    name: 'Selected Employee',
    department: '',
    branch: '',
    month,
    year,
    basicPay: 0,
    hra: 0,
    allowances: 0,
    totalEarnings: 0,
    pfDeduction: 0,
    taxDeduction: 0,
    totalDeductions: 0,
    netAmount: 0,
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    appToast.success(`Payslip PDF for ${targetRecord.name} downloaded successfully!`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-2xl rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl">receipt_long</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-on-surface">
                {record ? `Payslip — ${record.name}` : `Payslip Statement (${month} ${year})`}
              </h3>
              <p className="text-xs text-on-surface-variant font-medium">
                Official employee monthly salary breakdown statement.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface flex items-center justify-center transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Payslip Document Preview */}
        <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low/40 p-6 space-y-5 text-xs text-on-surface shadow-xs">
          
          {/* Org & Payslip Title */}
          <div className="text-center border-b border-outline-variant/20 pb-4">
            <h2 className="text-lg font-black tracking-tight text-primary font-display">TEZ EDUCATIONAL SCHOOL</h2>
            <p className="text-[11px] text-on-surface-variant">London Central Campus, Greater London, UK</p>
            <p className="text-xs font-bold text-on-surface mt-2 bg-surface-container-high inline-block px-3 py-1 rounded-full">
              Payslip for the Period of {targetRecord.month || month} {targetRecord.year || year}
            </p>
          </div>

          {/* Employee Meta Grid */}
          <div className="grid grid-cols-2 gap-4 bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/20">
            <div>
              <p className="text-[10px] uppercase font-bold text-on-surface-variant">Employee Name</p>
              <p className="text-sm font-bold text-on-surface">{targetRecord.name}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-on-surface-variant">Employee ID</p>
              <p className="text-sm font-bold text-on-surface">{targetRecord.idCard}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-on-surface-variant">Department</p>
              <p className="text-xs font-semibold text-on-surface">{targetRecord.department || 'Academic'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-on-surface-variant">Branch</p>
              <p className="text-xs font-semibold text-on-surface">{targetRecord.branch || 'Main Campus'}</p>
            </div>
          </div>

          {/* Earnings & Deductions Two-Column Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Earnings */}
            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden">
              <div className="bg-emerald-50 px-3 py-2 border-b border-outline-variant/15 text-xs font-bold text-emerald-800 uppercase tracking-wider">
                Earnings
              </div>
              <div className="p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">Basic Pay</span>
                  <span className="font-semibold tabular-nums">₹ {(targetRecord.basicPay || 55000).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">HRA</span>
                  <span className="font-semibold tabular-nums">₹ {(targetRecord.hra || 20000).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">Special Allowances</span>
                  <span className="font-semibold tabular-nums">₹ {(targetRecord.allowances || 10000).toLocaleString('en-IN')}</span>
                </div>
                <div className="border-t border-outline-variant/20 pt-2 flex justify-between font-bold text-emerald-700">
                  <span>Gross Earnings</span>
                  <span className="tabular-nums">₹ {(targetRecord.totalEarnings || 85000).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden">
              <div className="bg-rose-50 px-3 py-2 border-b border-outline-variant/15 text-xs font-bold text-rose-800 uppercase tracking-wider">
                Deductions
              </div>
              <div className="p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">Provident Fund (PF)</span>
                  <span className="font-semibold tabular-nums">₹ {(targetRecord.pfDeduction || 4500).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">Income Tax (TDS)</span>
                  <span className="font-semibold tabular-nums">₹ {(targetRecord.taxDeduction || 3000).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">Other Recovery</span>
                  <span className="font-semibold tabular-nums">₹ 0.00</span>
                </div>
                <div className="border-t border-outline-variant/20 pt-2 flex justify-between font-bold text-rose-600">
                  <span>Total Deductions</span>
                  <span className="tabular-nums">₹ {(targetRecord.totalDeductions || 7500).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Net Salary Summary Box */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-primary text-on-primary font-display">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">Net Salary Payable</span>
              <p className="text-xl font-extrabold tracking-tight">₹ {(targetRecord.netAmount || 77500).toLocaleString('en-IN')}</p>
            </div>
            <div className="text-right text-[11px] opacity-90">
              <span>Status: </span>
              <span className="font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded">Processed</span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between border-t border-outline-variant/20 pt-4">
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="px-4 py-2 rounded-xl bg-surface-container-high hover:bg-surface-container-highest text-on-surface text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">download</span>
            <span>Download PDF</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-xl bg-surface-container-high hover:bg-surface-container-highest text-on-surface text-xs font-bold transition cursor-pointer"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="px-6 py-2 rounded-xl bg-primary hover:bg-primary/90 text-on-primary text-xs font-bold shadow-sm transition flex items-center gap-1.5 cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">print</span>
              <span>Print Payslip</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
